import { useState, useEffect, useMemo, useRef, useCallback, type JSX } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router'
import { api } from '../../api/client.js'
import { getSocket, disconnectSocket } from '../../socket/client.js'
import { wireSocketEvents, resumeSession } from '../../socket/events.js'
import {
  LayoutDashboard,
  BarChart3,
  Server,
  MessageSquare,
  Headphones,
  Phone,
  Voicemail,
  UserCircle,
  Users,
  Contact,
  ScanSearch,
  RefreshCw,
  VolumeX,
  LogOut,
  Bell,
  Layers,
  MessageCircle,
  Plus,
  BookOpen,
} from 'lucide-react'
import AgentHeaderBar from './AgentHeaderBar.js'
import AgentSidebar from './AgentSidebar.js'
import AgentSoftphonePanel from './AgentSoftphonePanel.js'
import ChatOfferPopup from './ChatOfferPopup.js'
import LiveAssistPanel from '../live-assist/LiveAssistPanel.js'
import BreakAssistant from '../wfm/BreakAssistant.js'
import AiHandoffCard from '../voice/AiHandoffCard.js'
import IvrContextCard from '../integration/IvrContextCard.js'
import AgentVoiceAssistant from '../voice/AgentVoiceAssistant.js'
import Select from '../ui/Select.js'
import { usePhoneCall } from '../wfm/wfm-live-store.js'
import { useLiveAssist } from '../../modules/live-assist/useLiveAssist.js'
import AgentTabs, { type Tab } from './AgentTabs.js'
import NotificationToast from '../notifications/NotificationToast.js'
import { BrowserNotificationPrompt } from '../notifications/BrowserNotificationPrompt.js'
import { useSoftphone } from '../../modules/softphone/useSoftphone.js'
import { isCallWaiting } from '../../modules/softphone/call-waiting.js'
import { useAuthStore } from '../../store/authStore.js'
import { logout as logoutSession } from '../../auth/logout.js'
import { useCallStore } from '../../store/callStore.js'
import { CallWorkDrawer, type CallWorkStep } from './CallWorkDrawer.js'
import { TransferDrawer } from './TransferDrawer.js'
import { useAgentStore } from '../../store/agentStore.js'
import { useAgentStatus } from '../../hooks/useAgentStatus.js'
import type { SoftphoneConfig } from '../../modules/softphone/useSoftphone.js'
import { useSoftphoneStore } from '../../store/softphoneStore.js'
import { useWebRtcStats } from '../../modules/network-quality/index.js'
import { useToast } from '../system/ToastProvider.js'

// Map routes to tab metadata
const ROUTE_TAB_MAP: Record<string, { label: string; icon: React.ReactNode }> = {
  // Dashboard
  '/agent': { label: 'My Dashboard', icon: <LayoutDashboard className="h-[14px] w-[14px]" /> },
  '/agent/new-tab': { label: 'New Tab', icon: <Plus className="h-[14px] w-[14px]" /> },
  '/agent/dashboard/pbx': { label: 'PBX Dashboard', icon: <Server className="h-[14px] w-[14px]" /> },
  '/agent/dashboard/conversations': { label: 'Conversation Dashboard', icon: <MessageSquare className="h-[14px] w-[14px]" /> },
  '/agent/dashboard/call-center': { label: 'Call Center Dashboard', icon: <Headphones className="h-[14px] w-[14px]" /> },
  // Real Time Analytics — the sidebar has linked this since the page shipped,
  // but without an entry here the route opened with no tab and no active
  // highlight, so it looked like navigation had failed.
  '/agent/real-time-analytics/campaign-summary': { label: 'Campaign Summary', icon: <BarChart3 className="h-[14px] w-[14px]" /> },
  // Directory
  '/agent/company-directory': { label: 'Company Directory', icon: <Users className="h-[14px] w-[14px]" /> },
  '/agent/clients': { label: 'Clients', icon: <Contact className="h-[14px] w-[14px]" /> },
  '/agent/customers': { label: 'Customer 360', icon: <ScanSearch className="h-[14px] w-[14px]" /> },
  '/agent/chat': { label: 'Chat', icon: <MessageCircle className="h-[14px] w-[14px]" /> },
  '/agent/live-chat': { label: 'Web', icon: <MessageCircle className="h-[14px] w-[14px]" /> },
  '/agent/chats/whatsapp': { label: 'WhatsApp', icon: <MessageCircle className="h-[14px] w-[14px]" /> },
  '/agent/chats/facebook': { label: 'Facebook', icon: <MessageCircle className="h-[14px] w-[14px]" /> },
  '/agent/chats/instagram': { label: 'Instagram', icon: <MessageCircle className="h-[14px] w-[14px]" /> },
  // Communications
  // '/agent/workspace': { label: 'Workspace', icon: <BarChart3 className="h-[14px] w-[14px]" /> }, // Removed
  // '/agent/conversations': { label: 'Conversations', icon: <MessageSquare className="h-[14px] w-[14px]" /> }, // Removed
  '/agent/calls': { label: 'Recent Calls', icon: <Phone className="h-[14px] w-[14px]" /> },
  '/agent/voicemail': { label: 'Voicemail', icon: <Voicemail className="h-[14px] w-[14px]" /> },
  '/agent/knowledge-base': { label: 'Knowledge Base', icon: <BookOpen className="h-[14px] w-[14px]" /> },
  '/agent/notifications': { label: 'Notifications', icon: <Bell className="h-[14px] w-[14px]" /> },
  // '/agent/devices': { label: 'Devices', icon: <Settings className="h-[14px] w-[14px]" /> },
  '/agent/profile': { label: 'Profile', icon: <UserCircle className="h-[14px] w-[14px]" /> },
  // Conversation hub
  '/agent/conversation/inbox': { label: 'Inbox', icon: <MessageSquare className="h-[14px] w-[14px]" /> },
  '/agent/conversation/approvals': { label: 'Approvals', icon: <MessageSquare className="h-[14px] w-[14px]" /> },
  '/agent/conversation/reassignment': { label: 'Reassignment', icon: <MessageSquare className="h-[14px] w-[14px]" /> },
  '/agent/conversation/search': { label: 'Search Conversation', icon: <MessageSquare className="h-[14px] w-[14px]" /> },
  '/agent/conversation/omnichannel-queue': { label: 'Omnichannel Queue', icon: <Layers className="h-[14px] w-[14px]" /> },
  '/agent/reports': { label: 'Reports', icon: <BarChart3 className="h-[14px] w-[14px]" /> },
  '/agent/reports/conversation/agent-performance': { label: 'Agent Performance', icon: <BarChart3 className="h-[14px] w-[14px]" /> },
  '/agent/reports/conversation/detail': { label: 'Conversation Detail', icon: <BarChart3 className="h-[14px] w-[14px]" /> },
  '/agent/conversation/all': { label: 'All Conversations', icon: <Layers className="h-[14px] w-[14px]" /> },
}

// The Chrome-style launcher path. Navigating away from it to a real page
// replaces this tab with the destination (rather than leaving it open).
const NEW_TAB_PATH = '/agent/new-tab'
/** Prefix match for a dynamic-id route — each conversation opens as its own
 *  tab, so it can't use the static ROUTE_TAB_MAP (one label per route). The
 *  label is read from history state (set by the navigate() call that opened
 *  it, e.g. the conversation's conversation reference) and falls back to a plain
 *  "Conversation" label on a direct URL load/refresh with no state. */
const CONVERSATION_TAB_PREFIX = '/agent/conversation/view/'

/** Prefix match for each social chat channel — one tab per :chatId for Facebook/Instagram/WhatsApp. */
const CHAT_TAB_PREFIXES = [
  '/agent/chats/facebook/',
  '/agent/chats/instagram/',
  '/agent/chats/whatsapp/',
]

/**
 * The tab a given location belongs to. Conversations own their exact path (one
 * tab per id); every other route collapses to its ROUTE_TAB_MAP base, since
 * `/agent/company-directory/<id>` and `/agent/company-directory` share one tab.
 * Used to record a new tab's `openedFrom`, so closing it can find that tab
 * again by path.
 */
function resolveTabPath(pathname: string): string {
  if (pathname.startsWith(CONVERSATION_TAB_PREFIX)) return pathname
  if (CHAT_TAB_PREFIXES.some((p) => pathname.startsWith(p))) return pathname
  const routes = Object.keys(ROUTE_TAB_MAP).sort((a, b) => b.length - a.length)
  for (const route of routes) {
    if (pathname === route || pathname.startsWith(`${route}/`)) return route
  }
  return pathname
}

export default function AgentLayout(): JSX.Element {
  const toast = useToast()
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [rightSidebarCollapsed, setRightSidebarCollapsed] = useState(true)
  const location = useLocation()
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const userStatus = useAgentStore((s) => s.userStatus)
  const [recordingPaused, setRecordingPaused] = useState(false)
  const [recordingActive, setRecordingActive] = useState(false)
  const [recordingLoading, setRecordingLoading] = useState(false)
  const [callWaiting, setCallWaiting] = useState(false)
  // Manual contact/ticket drawer — opened by the softphone's
  // New Contact / New Ticket buttons.
  const [callWork, setCallWork] = useState<{ open: boolean; step: CallWorkStep }>({
    open: false,
    step: 'contact',
  })
  // Transfer drawer — opened by the softphone's Xfer rail button. Lives at
  // layout level for the same transform reason as CallWorkDrawer.
  const [transferOpen, setTransferOpen] = useState(false)
  
  // Initialize agent status (loads break codes and session timer)
  useAgentStatus()

  // Own the socket connection + event wiring for the whole /agent/* shell so
  // realtime pushes (notifications, conversation updates, etc.) work on every
  // route, not just the /agent/workspace page that used to own this.
  const socketWiredRef = useRef(false)
  useEffect(() => {
    // Guard against React 18/19 StrictMode double-invocation in dev.
    if (socketWiredRef.current) return
    socketWiredRef.current = true

    const socket = getSocket()
    const cleanup = wireSocketEvents(socket)
    socket.emit('agent:status', { event: 'login' })
    // Fresh-page-load probe — `connectionStateRecovery` only fires
    // for a brief disconnect; full reloads need an explicit ask.
    resumeSession(socket)

    // Heartbeat: ws-gateway's AgentPresenceStore session key (which the
    // ghost-agent-reaper and the round-robin available-agents SET both
    // depend on) carries a 30s TTL that only server.ts's `ping` handler
    // refreshes (server.ts:583) — nothing on this side was ever calling
    // it, so every agent's session silently expired ~90s after login and
    // the reaper evicted them from the available pool a minute later,
    // even though the UI kept showing them as Available. 15s keeps
    // comfortably under the 30s TTL even with one dropped tick.
    const heartbeatId = setInterval(() => {
      socket.emit('ping')
    }, 15_000)

    return () => {
      clearInterval(heartbeatId)
      cleanup()
      disconnectSocket()
      socketWiredRef.current = false
    }
  }, [])

  // PC-06: the call is still being recorded because the pause failed. Tell the agent until they dismiss it.
  const [pciPauseFailed, setPciPauseFailed] = useState<string | null>(null)
  useEffect(() => {
    const socket = getSocket()
    const onFailed = (e: { message: string }) => setPciPauseFailed(e.message)
    socket.on('pci:pause:failed', onFailed)
    return () => { socket.off('pci:pause:failed', onFailed) }
  }, [])

  // Cache the current agent's real agents.id (distinct from the JWT
  // sub/user.id, which is a users.id) so non-component modules like
  // socket/events.ts can read "is this event about me" synchronously via
  // useAuthStore.getState(). Always fetch on mount to ensure agentId is
  // current even if a stale value was persisted from a previous session.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const res = await api.get<{ id: string }>('/agent/me')
        if (!cancelled && res?.id) {
          useAuthStore.getState().setAgentId(res.id)
        }
      } catch {
        // Non-fatal — approval-count live updates degrade gracefully to
        // "refetch on dropdown open" (the pre-existing behavior) if this
        // never resolves; nothing else in the shell depends on agentId.
      }
    })()
    return () => { cancelled = true }
  }, [])

  // Call waiting: when a call:incoming event arrives while the agent is
  // already on an active call (answered or on_hold), set callWaiting so
  // the softphone panel shows the waiting indicator with accept/reject.
  const activeCall = useCallStore((s) => s.activeCall)
  const incomingCall = useCallStore((s) => s.incomingCall)
  // Portal status mirrored onto the softphone as its telephony status:
  // selecting Available/Break/Lunch in the header must visibly change the
  // phone panel, not just the dropdown it was clicked in.
  const agentUserStatus = useAgentStore((s) => s.userStatus)
  useEffect(() => {
    // Compare call IDs: `incomingCall` still refers to the CURRENT call after
    // `activateFromIncoming()` consumes it, so without this the answered call
    // reported itself as a second call waiting. See `call-waiting.ts`.
    //
    // Assigned unconditionally — the old version only cleared the flag when
    // `incomingCall` went away, so once it latched on it survived the active
    // call ending.
    setCallWaiting(
      isCallWaiting({
        incomingCallId: incomingCall?.callId,
        activeCallId: activeCall?.callId,
        activeStatus: activeCall?.status,
      }),
    )
  }, [incomingCall, activeCall])

  /*
   * CALLER LOOKUP — resolve the caller's number to a `clients` row.
   *
   * Client-portal calls arrive with clientId/callerDisplayName already
   * resolved by the gateway; a PSTN call is just a number. Looking it up
   * here is what lets the softphone show the saved NAME on an incoming call
   * and hide "New Contact" for callers that already exist.
   */
  const [callerClient, setCallerClient] = useState<{ id: string; fullName: string | null; email?: string | null } | null>(null)

  // A ringing call MUST reveal its own controls.
  //
  // THE DEFECT THIS FIXES
  // ---------------------
  // The softphone is a floating panel gated on `rightSidebarCollapsed`, which
  // starts TRUE — and while collapsed the panel is `opacity-0
  // pointer-events-none`, so it is not merely hidden, it cannot be clicked.
  //
  // Answer and Reject live inside that panel (AgentSoftphonePanel's
  // `callState === 'ringing_in'` block) and are the ONLY accept/reject controls
  // in the app: SoftphoneOverlay and SoftphonePopup are orphaned alternatives
  // with zero importers (see orphan-components.test.ts).
  //
  // So an incoming call played a ringtone while its buttons stayed invisible.
  // The agent could hear the call and had no way to take it or decline it —
  // reported as "it auto answers and there is no option to receive or cut". The
  // ringtone made it worse by proving the call existed.
  //
  // Opening on `in_call` too, because hangup, mute, hold and DTMF live in the
  // same panel: a call answered from elsewhere (auto-answer, or a desk phone)
  // must not leave the agent with no way to end it.
  //
  // Deliberately one-way. It never re-collapses the panel, so an agent who
  // opens it mid-call keeps it, and dismissing it during a call stays possible.
  //
  // Declared below, next to `softphone` — reading it here would hit the
  // temporal dead zone and throw on every mount.

  // Initialize tabs with My Dashboard tab so there's always at least one
  const [tabs, setTabs] = useState<Tab[]>(() => [
    {
      id: 'dashboard-init',
      path: '/agent',
      label: 'My Dashboard',
      icon: <LayoutDashboard className="h-[14px] w-[14px]" />,
    }
  ])
  
  // Only show navigation links when status is Available
  const showNavigation = userStatus.key === 'available'

  // In dev (bare `vite`, no reverse proxy in front), the same-origin
  // /ws-sip path can't be proxied through Vite's own dev server — Vite's
  // dev server intercepts/interferes with WebSocket upgrades on arbitrary
  // paths regardless of proxy config (see vitejs/vite#864, vitejs/vite#4124;
  // confirmed via a minimal raw-WS echo server while debugging the client
  // portal's identical wsUrl issue — direct connections succeed, the same
  // request proxied through Vite always closes with code 1006 even though
  // the target receives it correctly). Kamailio's WS listener (port 5062,
  // plain ws) is already published directly by docker-compose for local
  // dev, so connect to it directly there instead. In a real deployment
  // (import.meta.env.PROD), nginx/ingress fronts the app and CAN proxy
  // /ws-sip correctly, so the same-origin path is used there — matching
  // apps/web/src/modules/softphone/useClientCall.ts's identical fix.
  const wsUrl =
    (import.meta.env['VITE_KAMAILIO_WSS'] as string | undefined) ??
    (import.meta.env.DEV
      ? `ws://${window.location.hostname}:5062`
      : `wss://${window.location.hostname}/ws-sip`)

  const sipConfig: SoftphoneConfig | null =
    user
      ? {
          agentId: user.sub,
          tenantDomain: `${user.tenantSlug}.ccs.internal`,
          wsUrl,
          // ITEM 99 — completes the chain: agents table -> /auth/me ->
          // authStore -> here -> useSoftphone. The flag was previously
          // settable, persisted, and read by nothing.
          autoAnswerIncomingCalls: user.autoAnswerIncomingCalls ?? false,
        }
      : null

  const softphone = useSoftphone(sipConfig)
  const [callDetails, setCallDetails] = useState<{
    callId: string; direction: string; remoteNumber: string; did: string | null; queueName: string | null; bridgeId: string | null
  } | null>(null)
  useEffect(() => {
    setCallDetails(null)
    if (softphone.callState === 'idle' || softphone.echoTestRunning) return
    let cancelled = false
    let timer: ReturnType<typeof setTimeout>
    const load = async () => {
      try {
        const res = await api.get<{ call: typeof callDetails }>('/agent/calls/current')
        console.log('[AgentLayout] Call details loaded:', res.call)
        if (!cancelled) setCallDetails(res.call)
      } catch {
        console.log('[AgentLayout] Failed to load call details')
        if (!cancelled) setCallDetails(null)
      }
      if (!cancelled) timer = setTimeout(() => void load(), 2000)
    }
    void load()
    return () => { cancelled = true; clearTimeout(timer) }
  }, [softphone.callState, softphone.remoteCLI, softphone.echoTestRunning])

  const matchedActiveCall = activeCall?.status !== 'ended' && (!callDetails || activeCall?.callId === callDetails.callId) ? activeCall : null
  const liveCallId = callDetails?.callId ?? matchedActiveCall?.callId ?? null
  const callerCLI = softphone.callState === 'idle' ? null : callDetails?.remoteNumber || softphone.remoteCLI || matchedActiveCall?.callerCLI || null

  // Debug logging for call ID resolution
  useEffect(() => {
    console.log('[AgentLayout] Call ID resolution:', {
      callDetailsCallId: callDetails?.callId,
      activeCallId: activeCall?.callId,
      matchedActiveCallId: matchedActiveCall?.callId,
      liveCallId,
      softphoneCallState: softphone.callState,
    })
  }, [callDetails?.callId, activeCall?.callId, matchedActiveCall?.callId, liveCallId, softphone.callState])
  useEffect(() => {
    setCallerClient(null)
    if (softphone.callState === 'idle') return
    if (matchedActiveCall?.clientId) {
      // Already resolved by the gateway — honour it and skip the REST call.
      setCallerClient({ id: matchedActiveCall.clientId, fullName: matchedActiveCall.callerDisplayName ?? null })
      return
    }
    const phone = callerCLI?.trim()
    if (!phone) return
    let cancelled = false
    api.get<{ client: typeof callerClient }>(`/agent/contacts/lookup?phone=${encodeURIComponent(phone)}`)
      .then((res) => { if (!cancelled) setCallerClient(res.client) })
      .catch(() => { if (!cancelled) setCallerClient(null) })
    return () => { cancelled = true }
  }, [callerCLI, softphone.callState, matchedActiveCall?.callId, matchedActiveCall?.clientId, matchedActiveCall?.callerDisplayName])

  // Synchronize recording state with backend and socket events
  useEffect(() => {
    const inCall = softphone.callState === 'in_call' || softphone.callState === 'on_hold'
    if (!inCall) {
      setRecordingPaused(false)
      setRecordingActive(false)
      return
    }

    if (user?.allowAgentPause) {
      setRecordingActive(true)
    }

    let active = true
    const checkStatus = async () => {
      try {
        const url = liveCallId
          ? `/calls/${liveCallId}/recording/status`
          : `/recordings/status?extension=${encodeURIComponent(user?.extension || '')}&agentId=${encodeURIComponent(user?.sub || '')}`
        const res = await api.get<{
          status?: 'RECORDING' | 'PAUSED' | 'STOPPED' | 'FAILED' | 'UNKNOWN'
          state?: 'RECORDING' | 'PAUSED' | 'STOPPED' | 'FAILED' | 'UNKNOWN'
          allowAgentPause?: boolean
        }>(url)
        const currentStatus = res.status ?? res.state
        console.log('[AgentLayout:Recording] Polling status:', { url, status: currentStatus, allowAgentPause: res.allowAgentPause })
        if (!active) return

        if (res.allowAgentPause !== undefined && user?.allowAgentPause !== res.allowAgentPause) {
          const currentUser = useAuthStore.getState().user
          if (currentUser) {
            useAuthStore.setState({
              user: { ...currentUser, allowAgentPause: Boolean(res.allowAgentPause) },
            })
          }
        }

        if (currentStatus === 'RECORDING') {
          setRecordingActive(true)
          setRecordingPaused(false)
        } else if (currentStatus === 'PAUSED') {
          setRecordingActive(true)
          setRecordingPaused(true)
        } else if (currentStatus === 'STOPPED' || currentStatus === 'FAILED') {
          if ((res.allowAgentPause ?? user?.allowAgentPause) && inCall) {
            setRecordingActive(true)
          } else {
            setRecordingActive(false)
          }
          setRecordingPaused(false)
        }
      } catch (err: unknown) {
        console.warn('[AgentLayout:Recording] Status polling failed:', err)
      }
    }

    void checkStatus()
    const interval = setInterval(() => {
      void checkStatus()
    }, 4000)

    const rawSocket = getSocket() as unknown as {
      on: (ev: string, fn: (e: { callId?: string }) => void) => void
      off: (ev: string, fn: (e: { callId?: string }) => void) => void
    }
    const onPaused = (e: { callId?: string }) => {
      if (!e?.callId || e.callId === liveCallId) {
        setRecordingPaused(true)
        setRecordingActive(true)
      }
    }
    const onResumed = (e: { callId?: string }) => {
      if (!e?.callId || e.callId === liveCallId) {
        setRecordingPaused(false)
        setRecordingActive(true)
      }
    }
    rawSocket.on('recording:paused', onPaused)
    rawSocket.on('recording:resumed', onResumed)

    return () => {
      active = false
      clearInterval(interval)
      rawSocket.off('recording:paused', onPaused)
      rawSocket.off('recording:resumed', onResumed)
    }
  }, [liveCallId, softphone.callState, user?.allowAgentPause, user?.extension, user?.sub])

  useEffect(() => {
    if (softphone.callState === 'idle') setTransferOpen(false)
  }, [softphone.callState])
  const setUserStatus = useAgentStore((s) => s.setUserStatus)

  // Reveal the softphone panel while a call needs the agent's hands.
  // See the note above `callWaiting` for why this is required: the panel is
  // `pointer-events-none` while collapsed, so Answer/Reject/Hangup are not just
  // hidden but unclickable, and the panel starts collapsed.
  useEffect(() => {
    if (softphone.callState === 'ringing_in' || softphone.callState === 'in_call') {
      setRightSidebarCollapsed(false)
    }
  }, [softphone.callState])

  // ITEM 100 — drive the WebRTC stats poller.
  //
  // `useWebRtcStats` was complete — it polls getStats(), derives packet loss,
  // jitter, RTT and a MOS via the E-model, and emits `webrtc:stats` over the
  // socket — but NOTHING called it. Meanwhile AgentHeaderBar renders
  // <NetworkQualityMeter />, so the UI was present and permanently empty.
  //
  // Passing null (no active call) is handled inside the hook: it simply does
  // not start the interval.
  useWebRtcStats(softphone.peerConnection)

  // Real-time in-call AI assist — taps this call's own WebRTC tracks and
  // streams them to the ws-gateway for live transcription + insights.
  // Mirror the softphone call state for WFM views (softphone calls don't
  // change the agent's server status).
  useEffect(() => { usePhoneCall.getState().set(softphone.callState) }, [softphone.callState])

  const liveAssist = useLiveAssist({
    peerConnection: softphone.echoTestRunning ? null : softphone.peerConnection,
    active: (softphone.callState === 'in_call' || softphone.callState === 'on_hold') && !softphone.echoTestRunning,
    callRef: callDetails?.callId ?? matchedActiveCall?.callId ?? null,
    customerNumber: callerCLI,
    direction: callDetails?.direction ?? null,
  })

  // ITEM 97 — publish the softphone ACTIONS so components off the prop chain
  // can place calls.
  //
  // The hook stays here, at the root: it owns a JsSIP UA, media elements and
  // device state, and a second instance would register a second SIP endpoint for
  // this agent. What changes is that reaching it no longer requires an unbroken
  // prop chain from this component — only three sites had one, so click-to-call,
  // directory transfer and internal calls had no route to the softphone at all.
  //
  // Only actions are published, never call state: mirroring `callState` here
  // would create a second source of truth that drifts from the hook.
  const registerSoftphone = useSoftphoneStore((s) => s.register)
  const unregisterSoftphone = useSoftphoneStore((s) => s.unregister)
  useEffect(() => {
    registerSoftphone({
      dial: softphone.dial,
      hangup: softphone.hangup,
      transfer: softphone.transfer,
      sendDtmf: softphone.sendDtmf,
    })
    // Cleared on unmount so no closure outlives the UA it captured — on logout
    // or a config change the old hook's JsSIP instance is being torn down.
    return () => unregisterSoftphone()
  }, [
    softphone.dial,
    softphone.hangup,
    softphone.transfer,
    softphone.sendDtmf,
    registerSoftphone,
    unregisterSoftphone,
  ])

  const softphoneSidebarProps = useMemo(() => {
    if (!user?.allowTelephony) return null
    return {
      registered: softphone.registered,
      callState: softphone.callState,
      remoteCLI: softphone.remoteCLI,
      isMuted: softphone.isMuted,
      callStartTime: softphone.callStartTime,
      onDial: softphone.dial,
      onAnswer: softphone.answer,
      onHangup: softphone.echoTestRunning
      ? softphone.stopEchoTest
      : softphone.hangup,
      onHold: softphone.hold,
      onUnhold: softphone.unhold,
      onMute: softphone.mute,
      onUnmute: softphone.unmute,
      onSendDtmf: softphone.sendDtmf,
      audioInputDevices: softphone.audioInputDevices,
      audioOutputDevices: softphone.audioOutputDevices,
      selectedAudioInput: softphone.selectedAudioInput,
      selectedAudioOutput: softphone.selectedAudioOutput,
      onSelectAudioInput: softphone.setSelectedAudioInput,
      onSelectAudioOutput: softphone.setSelectedAudioOutput,
      onRefreshDevices: softphone.refreshDevices,
      onMakeAvailable: () => {
        setUserStatus({ key: 'available', text: 'Available' })
        getSocket().emit('agent:status', { event: 'unpause' })
      },
      canMakeAvailable: userStatus.key !== 'available',
      echoTestPassed: softphone.echoTestPassed,
      echoTestRunning: softphone.echoTestRunning,
      // UI-4+5 — recording controls
      allowAgentPause: user?.allowAgentPause ?? false,
      recordingPaused,
      recordingActive,
      onRecordingPause: async () => {
        if (recordingLoading) return
        setRecordingLoading(true)
        const url = liveCallId ? `/calls/${liveCallId}/recording/pause` : '/recordings/pause'
        const payload = {
          agentId: user?.sub,
          ...(user?.extension ? { extension: user.extension } : {}),
          ...(liveCallId ? { callId: liveCallId } : {}),
          ...(callDetails?.bridgeId ? { bridgeId: callDetails.bridgeId } : {}),
          reason: 'PCI' as const,
        }
        console.log('[AgentLayout:Recording] Requesting recording pause:', { url, payload })
        try {
          const res = await api.post(url, payload)
          console.log('[AgentLayout:Recording] Recording pause succeeded:', res)
          setRecordingPaused(true)
          setRecordingActive(true)
          toast.info('Call recording has been paused.')
        } catch (err: unknown) {
          console.error('[AgentLayout:Recording] Failed to pause recording:', err)
          toast.error('PCI Security Warning: Recording pause FAILED. Audio muting is NOT confirmed. DO NOT collect card data on this call.')
        } finally {
          setRecordingLoading(false)
        }
      },
      onRecordingResume: async () => {
        if (recordingLoading) return
        setRecordingLoading(true)
        const url = liveCallId ? `/calls/${liveCallId}/recording/resume` : '/recordings/resume'
        const payload = {
          agentId: user?.sub,
          ...(user?.extension ? { extension: user.extension } : {}),
          ...(liveCallId ? { callId: liveCallId } : {}),
          ...(callDetails?.bridgeId ? { bridgeId: callDetails.bridgeId } : {}),
        }
        console.log('[AgentLayout:Recording] Requesting recording resume:', { url, payload })
        try {
          const res = await api.post(url, payload)
          console.log('[AgentLayout:Recording] Recording resume succeeded:', res)
          setRecordingPaused(false)
          setRecordingActive(true)
          toast.success('Call recording has resumed.')
        } catch (err: unknown) {
          console.error('[AgentLayout:Recording] Failed to resume recording:', err)
          toast.error('Failed to resume recording.')
        } finally {
          setRecordingLoading(false)
        }
      },
      onRecordingStart: async () => {
        try {
          console.log('[Recording] Starting on-demand recording with callId:', liveCallId)
          await api.post('/recordings/on-demand/start', {
            agentId: user?.sub,
            ...(liveCallId ? { callId: liveCallId } : {}),
          })
          setRecordingActive(true)
          setRecordingPaused(false)
          toast.success('Call recording started.')
        } catch (err: unknown) {
          console.error('Failed to start on-demand recording:', err)
          toast.error('Failed to start on-demand recording.')
        }
      },
      onRecordingStop: async () => {
        try {
          await api.post('/recordings/on-demand/stop', {
            agentId: user?.sub,
            ...(liveCallId ? { callId: liveCallId } : {}),
          })
          setRecordingActive(false)
          toast.info('Call recording stopped.')
        } catch (err: unknown) {
          console.error('Failed to stop on-demand recording:', err)
          toast.error('Failed to stop on-demand recording.')
        }
      },
      // UI-6 — call waiting indicator
      callWaiting,
      onAcceptWaitingCall: () => {
        // Accept the waiting call — put current call on hold and answer
        if (softphone.callState === 'in_call') {
          softphone.hold()
        }
        softphone.answer()
        setCallWaiting(false)
      },
      onRejectWaitingCall: () => {
        // Reject the waiting call — hangup the waiting session
        setCallWaiting(false)
      },
    }
  }, [softphone, user?.extension, userStatus.key, setUserStatus, recordingPaused, recordingActive, callWaiting, user?.sub, user?.allowAgentPause, liveCallId])

  /* The softphone is a docked right-side drawer now — the dialer button just
   * toggles it; the anchor arg is kept only so the header's signature doesn't
   * change. */
  const toggleSoftphonePanel = useCallback(() => {
    setRightSidebarCollapsed((v) => !v)
  }, [])

  // Auto-open softphone on incoming call or echo test call
  useEffect(() => {
    if (softphone.callState === 'ringing_in') {
      setRightSidebarCollapsed(false)
    }
    if (softphone.echoTestRunning) {
      setRightSidebarCollapsed(false)
    }
  }, [softphone.callState, softphone.echoTestRunning])

  const showEchoTestModal =
    softphoneSidebarProps !== null &&
    user?.allowTelephony && // Only show if user has telephony enabled
    userStatus.key === 'available' &&
    !softphone.echoTestPassed &&
    !softphone.echoTestRunning &&
    softphone.callState === 'idle'

  // Track the previously-resolved base route so we can tell when the user
  // navigated *out of* the New Tab launcher (Chrome-style tab replacement).
  const prevBaseRouteRef = useRef<string | null>(null)

  // The path that was active before this navigation. A tab records it as its
  // `openedFrom` so closing the tab can return the user to where they opened
  // it. Updated by an effect declared *after* the tab effect below, so the tab
  // effect still sees the previous path on the render that creates the tab.
  const previousPathRef = useRef<string | null>(null)

  // Add current route as tab when location changes
  useEffect(() => {
    const currentPath = location.pathname
    // Read the ref HERE, not inside the setTabs updater below.
    //
    // React invokes a functional updater lazily, during the render that
    // processes the update — by which point the effect declared after this one
    // has already advanced `previousPathRef` to the current path. Every tab was
    // therefore stamped with its own path as `openedFrom`, so closing it found
    // no such tab (it had just been removed) and fell through to whichever tab
    // happened to occupy that slot. Opening a conversation from the inbox and
    // closing it landed you on Approvals.
    const openedFromPath = previousPathRef.current
      ? resolveTabPath(previousPathRef.current)
      : null

    // Per-conversation tabs: one tab per :id, keyed by the full path (not
    // deduped to a single "conversation" tab like the static routes below).
    if (currentPath.startsWith(CONVERSATION_TAB_PREFIX)) {
      const state = location.state as { tabLabel?: string } | null
      // Without a label from navigate state (e.g. opened straight from a
      // notification deep link), fall back to the conversation id from the
      // path — ConversationTabPage relabels it to #<referenceId> as soon as
      // the conversation loads.
      const label = state?.tabLabel ?? currentPath.slice(CONVERSATION_TAB_PREFIX.length)
      setTabs((prev) => {
        const existing = prev.find((t) => t.path === currentPath)
        if (existing) return prev
        const newTab: Tab = {
          id: `${currentPath}-${Date.now()}`,
          path: currentPath,
          label,
          icon: <MessageSquare className="h-[14px] w-[14px]" />,
          ...(openedFromPath ? { openedFrom: openedFromPath } : {}),
        }
        return [...prev, newTab]
      })
      return
    }

    // Per-chat tabs: one tab per :id for Facebook/Instagram/WhatsApp.
    const chatPrefix = CHAT_TAB_PREFIXES.find((p) => currentPath.startsWith(p))
    if (chatPrefix) {
      const state = location.state as { tabLabel?: string } | null
      const basePath = currentPath.split('/').slice(0, -1).join('/') || currentPath
      const baseMeta = ROUTE_TAB_MAP[basePath]
      const label = state?.tabLabel ?? currentPath.slice(chatPrefix.length)
      setTabs((prev) => {
        const existing = prev.find((t) => t.path === currentPath)
        if (existing) return prev
        const newTab: Tab = {
          id: `${currentPath}-${Date.now()}`,
          path: currentPath,
          label,
          icon: baseMeta?.icon ?? <MessageCircle className="h-[14px] w-[14px]" />,
          ...(openedFromPath ? { openedFrom: openedFromPath } : {}),
        }
        return [...prev, newTab]
      })
      return
    }

    // Find base route - sort by length descending so /agent/calls matches before /agent
    let baseRoute = currentPath
    const sortedRoutes = Object.keys(ROUTE_TAB_MAP).sort((a, b) => b.length - a.length)
    for (const route of sortedRoutes) {
      if (currentPath === route || currentPath.startsWith(`${route}/`)) {
        baseRoute = route
        break
      }
    }

    const tabMetadata = ROUTE_TAB_MAP[baseRoute]
    if (!tabMetadata) return

    // If we just left the New Tab launcher for a real page, the launcher tab
    // becomes that page's tab instead of sticking around as a separate tab.
    const cameFromNewTab =
      prevBaseRouteRef.current === NEW_TAB_PATH && baseRoute !== NEW_TAB_PATH
    prevBaseRouteRef.current = baseRoute

    // Use functional update to check against latest state and avoid stale closure
    setTabs((prev) => {
      const base = cameFromNewTab ? prev.filter((t) => t.path !== NEW_TAB_PATH) : prev
      const existing = base.find((t) => t.path === baseRoute)
      if (existing) return base
      const newTab: Tab = {
        id: `${baseRoute}-${Date.now()}`,
        path: baseRoute,
        label: tabMetadata.label,
        icon: tabMetadata.icon,
        ...(openedFromPath ? { openedFrom: openedFromPath } : {}),
      }
      return [...base, newTab]
    })
  }, [location.pathname, location.state])

  // Declared after the tab effect so that effect reads the pre-navigation path.
  // The value is now captured synchronously in the effect body above rather
  // than inside its state updater, which is what makes the ordering reliable.
  useEffect(() => {
    previousPathRef.current = location.pathname
  }, [location.pathname])

  const handleCloseTab = (tabId: string) => {
    const index = tabs.findIndex((t) => t.id === tabId)
    if (index === -1) return
    const tabToClose = tabs[index]!

    const newTabs = tabs.filter((t) => t.id !== tabId)
    setTabs(newTabs)

    // Closing a background tab leaves you where you are.
    const isClosingActive = location.pathname === tabToClose.path ||
                           location.pathname.startsWith(`${tabToClose.path}/`)
    if (!isClosingActive) return

    // Go back to the tab this one was opened from — closing a conversation
    // opened from the inbox belongs on the inbox, not on whichever tab happens
    // to sit last in the bar (usually My Dashboard, which is where the agent
    // kept landing). If that tab is gone, fall back to the neighbour the way
    // browser tabs do: the one that slid into this slot, else the one before.
    const openedFrom = newTabs.find((t) => t.path === tabToClose.openedFrom)
    const neighbour = newTabs[index] ?? newTabs[index - 1]
    const target = openedFrom ?? neighbour
    navigate(target ? target.path : '/agent')
  }

  // Close a tab by matching its path. Used by child routes (e.g.
  // ConversationTabPage) that don't know the tab ID but know their own path.
  const closeTabByPath = (path: string) => {
    const tab = tabs.find((t) => t.path === path)
    if (tab) handleCloseTab(tab.id)
  }

  // Relabel a tab once the child route knows its real title — a conversation
  // opened from a notification deep link has no reference id to label with
  // until its detail loads.
  const setTabLabelByPath = useCallback((path: string, label: string) => {
    setTabs((prev) =>
      prev.some((t) => t.path === path && t.label !== label)
        ? prev.map((t) => (t.path === path ? { ...t, label } : t))
        : prev,
    )
  }, [])

  // Closes every tab except My Dashboard, which always stays open as the
  // landing tab (mirrors browser/IDE "close all tabs" behavior — never
  // leaves a fully empty tab bar).
  const handleCloseAllTabs = () => {
    const dashboardTab = tabs.find((t) => t.path === '/agent') ?? {
      id: 'dashboard-init',
      path: '/agent',
      label: 'My Dashboard',
      icon: <LayoutDashboard className="h-[14px] w-[14px]" />,
    }
    setTabs([dashboardTab])
    navigate(dashboardTab.path)
  }

  return (
    <div className="flex h-screen flex-col bg-slate-50">
      {pciPauseFailed && (
        <div
          role="alert"
          data-testid="pci-pause-failed"
          className="flex items-center justify-between gap-3 bg-red-600 px-4 py-2 text-sm font-semibold text-white"
        >
          <span>Recording could NOT be paused. Stop taking card details on this call now. {pciPauseFailed}</span>
          <button type="button" onClick={() => setPciPauseFailed(null)} className="rounded bg-white/20 px-2 py-0.5 text-xs">
            Dismiss
          </button>
        </div>
      )}
      <AgentHeaderBar
        softphone={softphone}
        sidebarCollapsed={sidebarCollapsed}
        onToggleLeftSidebar={() => setSidebarCollapsed((v) => !v)}
        onToggleRightSidebar={toggleSoftphonePanel}
      />
      <div className="relative flex flex-1 overflow-hidden">
        <AgentSidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed((v) => !v)}
          showLinks={showNavigation}
        />
        <div className="flex flex-1 flex-col overflow-hidden">
          {showNavigation && (
            <AgentTabs
              tabs={tabs}
              onCloseTab={handleCloseTab}
              onCloseAllTabs={handleCloseAllTabs}
              onNavigate={(path) => navigate(path)}
            />
          )}
          <main className="flex-1 overflow-auto bg-white">
            <Outlet context={{ closeCurrentTab: closeTabByPath, setTabLabel: setTabLabelByPath }} />
          </main>
        </div>
        {/* Softphone — docked right-side drawer (the legacy dialer layout),
            not a floating draggable popup. Slides in/out via translate-x; the
            header's dialer button and call events toggle it. */}
        {softphoneSidebarProps && (
          <div
            role="complementary"
            aria-label="Softphone drawer"
            aria-hidden={rightSidebarCollapsed}
            inert={rightSidebarCollapsed}
            onKeyDown={(event) => {
              if (event.key === 'Escape' && !event.defaultPrevented) setRightSidebarCollapsed(true)
            }}
            className={`fixed inset-y-0 right-0 z-50 flex w-[370px] max-w-full flex-col border-l border-slate-200 bg-white shadow-xl transition-transform duration-200 ease-out motion-reduce:transition-none ${
              rightSidebarCollapsed ? 'translate-x-full' : 'translate-x-0'
            }`}
          >
            <AgentSoftphonePanel
              onClose={() => setRightSidebarCollapsed(true)}
              registered={softphoneSidebarProps.registered}
              callState={softphoneSidebarProps.callState}
              remoteCLI={callerCLI}
              callId={callDetails?.callId ?? matchedActiveCall?.callId ?? null}
              callerDisplayName={callerClient?.fullName ?? matchedActiveCall?.callerDisplayName ?? null}
              callerEmail={callerClient?.email ?? matchedActiveCall?.callerEmail ?? null}
              queueName={callDetails?.queueName ?? null}
              did={callDetails?.did ?? null}
              callerIsClient={callerClient != null}
              agentStatus={agentUserStatus}
              isMuted={softphoneSidebarProps.isMuted}
              callStartTime={softphoneSidebarProps.callStartTime}
              onDial={softphoneSidebarProps.onDial}
              onAnswer={softphoneSidebarProps.onAnswer}
              onHangup={softphoneSidebarProps.onHangup}
              onHold={softphoneSidebarProps.onHold}
              onUnhold={softphoneSidebarProps.onUnhold}
              onMute={softphoneSidebarProps.onMute}
              onUnmute={softphoneSidebarProps.onUnmute}
              onSendDtmf={softphoneSidebarProps.onSendDtmf}
              audioInputDevices={softphoneSidebarProps.audioInputDevices}
              audioOutputDevices={softphoneSidebarProps.audioOutputDevices}
              selectedAudioInput={softphoneSidebarProps.selectedAudioInput}
              selectedAudioOutput={softphoneSidebarProps.selectedAudioOutput}
              onSelectAudioInput={softphoneSidebarProps.onSelectAudioInput}
              onSelectAudioOutput={softphoneSidebarProps.onSelectAudioOutput}
              onRefreshDevices={softphoneSidebarProps.onRefreshDevices}
              onMakeAvailable={softphoneSidebarProps.onMakeAvailable}
              canMakeAvailable={softphoneSidebarProps.canMakeAvailable}
              echoTestPassed={softphoneSidebarProps.echoTestPassed}
              echoTestRunning={softphoneSidebarProps.echoTestRunning}
              allowAgentPause={softphoneSidebarProps.allowAgentPause}
              recordingPaused={softphoneSidebarProps.recordingPaused}
              recordingActive={softphoneSidebarProps.recordingActive}
              onRecordingPause={softphoneSidebarProps.onRecordingPause}
              onRecordingResume={softphoneSidebarProps.onRecordingResume}
              onRecordingStart={softphoneSidebarProps.onRecordingStart}
              onRecordingStop={softphoneSidebarProps.onRecordingStop}
              callWaiting={softphoneSidebarProps.callWaiting}
              onAcceptWaitingCall={softphoneSidebarProps.onAcceptWaitingCall}
              onRejectWaitingCall={softphoneSidebarProps.onRejectWaitingCall}
              onCreateContact={() => setCallWork({ open: true, step: 'contact' })}
              onCreateTicket={() => setCallWork({ open: true, step: 'ticket' })}
              onOpenTransfer={() => setTransferOpen(true)}
            />
          </div>
        )}
      </div>

      {/* Manual contact/ticket creation — opened from the softphone panel.
          Lives at layout level so the drawer owns the viewport edge rather
          than the phone's fixed 300px column. */}
      {/* Mount only while open — the drawer runs ToastProvider-dependent
          hooks even when closed, which breaks any host that lacks the
          provider (and needlessly fetches form data for a hidden UI). */}
      {callWork.open && (
      <CallWorkDrawer
        open={callWork.open}
        initialStep={callWork.step}
        callerPhone={callerCLI}
        callerEmail={matchedActiveCall?.callerEmail ?? null}
        callId={callDetails?.callId ?? matchedActiveCall?.callId ?? null}
        callerName={callerClient?.fullName ?? matchedActiveCall?.callerDisplayName ?? null}
        clientId={callerClient?.id ?? null}
        onContactSaved={(c) => setCallerClient({ id: c.id, fullName: c.name })}
        onClose={() => setCallWork((s) => ({ ...s, open: false }))}
      />
      )}

      {/* Call transfer — the softphone's Xfer button opens this. Fixed
          right-side slide-over; needs the live callId for the server-side
          transfer action and falls back to in-dialog REFER without one. */}
      {transferOpen && (
        <TransferDrawer
          open={transferOpen}
          callId={callDetails?.callId ?? matchedActiveCall?.callId ?? null}
          remoteNumber={callerCLI}
          onTransfer={softphone.transfer}
          onHangup={softphone.hangup}
          onResume={softphone.resumeForTransfer}
          onClose={() => setTransferOpen(false)}
        />
      )}

      {/* Notification Toast */}
      <NotificationToast />

      {/* Chat-escalation offer popup — global overlay, visible from any
          /agent/* route, mounted alongside the socket lifecycle this
          layout already owns. */}
      <ChatOfferPopup />

      {/* Post-hang-up ticket prompt for client-portal voice calls. Mounted
          globally so it appears regardless of which /agent/* route is active,
          same lifecycle pattern as ChatOfferPopup above. Mounted only while
          a prompt is pending — it runs ToastProvider-dependent hooks even
          when hidden, which breaks hosts without the provider. */}

      <LiveAssistPanel assist={liveAssist} />
      <AiHandoffCard callId={incomingCall?.callId ?? activeCall?.callId ?? null} />
      <IvrContextCard callId={incomingCall?.callId ?? activeCall?.callId ?? null} ani={incomingCall?.from ?? activeCall?.callerCLI ?? null} />
      {/* Schedule-driven break prompts — defers a due break while on a call. */}
      <BreakAssistant onCall={softphone.callState !== 'idle'} />
      <AgentVoiceAssistant onCall={softphone.callState !== 'idle'} />

      {/* Centered Echo Test Modal — above all content */}
      {showEchoTestModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-[560px] rounded-[3px] bg-white p-8 shadow-2xl">
            {/* Header */}
            <div className="mb-6 flex items-center gap-4">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-[3px] bg-emerald-50">
                <Headphones className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Audio Check</h3>
                <p className="text-sm text-slate-500">
                  Test your microphone and speakers before taking calls.
                </p>
              </div>
            </div>

            {/* Web Telephony Options */}
            <div className="mb-6 rounded-[3px] border border-slate-100 bg-slate-50/50 p-5">
              <h4 className="mb-4 text-xs font-bold uppercase tracking-wider text-slate-500">
                Web Telephony Options
              </h4>
              <div className="grid grid-cols-2 gap-x-6 gap-y-3">
                {[
                  { label: 'Use Stereo', value: softphone.useStereo, onChange: softphone.setUseStereo },
                  { label: 'Use STUN', value: softphone.useStun, onChange: softphone.setUseStun },
                  { label: 'Auto Gain Control', value: softphone.enableAgc, onChange: softphone.setEnableAgc },
                  { label: 'Echo Cancellation', value: softphone.enableEchoCancel, onChange: softphone.setEnableEchoCancel },
                  { label: 'Noise Suppression', value: softphone.enableNoiseSupp, onChange: softphone.setEnableNoiseSupp },
                  { label: 'Highpass Filter', value: softphone.enableHighpass, onChange: softphone.setEnableHighpass },
                ].map((opt) => (
                  <label key={opt.label} className="flex cursor-pointer items-center justify-between gap-3">
                    <span className="text-sm font-medium text-slate-700">{opt.label}</span>
                    <span className="relative inline-flex h-5 w-9 shrink-0">
                      <input
                        type="checkbox"
                        checked={opt.value}
                        onChange={(e) => opt.onChange(e.target.checked)}
                        className="peer sr-only"
                      />
                      <span className="absolute inset-0 rounded-full bg-slate-300 transition-colors peer-checked:bg-blue-500" />
                      <span className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Devices */}
            <div className="mb-6 rounded-[3px] border border-slate-100 bg-slate-50/50 p-5">
              <h4 className="mb-4 text-xs font-bold uppercase tracking-wider text-slate-500">
                Devices
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Audio-In Devices <span className="text-red-500">*</span>
                  </label>
                  <Select
                    ariaLabel="Audio-In Devices"
                    value={softphone.selectedAudioInput}
                    onChange={softphone.setSelectedAudioInput}
                  >
                    <option value="">Default</option>
                    {softphone.audioInputDevices.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>{d.label}</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700">
                    Audio-Out Devices <span className="text-red-500">*</span>
                  </label>
                  <Select
                    ariaLabel="Audio-Out Devices"
                    value={softphone.selectedAudioOutput}
                    onChange={softphone.setSelectedAudioOutput}
                  >
                    <option value="">Default</option>
                    {softphone.audioOutputDevices.map((d) => (
                      <option key={d.deviceId} value={d.deviceId}>{d.label}</option>
                    ))}
                  </Select>
                </div>
                <button
                  onClick={softphone.refreshDevices}
                  className="flex items-center gap-2 rounded-[3px] bg-blue-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh Devices
                </button>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              {!softphone.echoTestRunning ? (
                <button
                  onClick={softphone.runEchoTest}
                  className="flex w-full items-center justify-center gap-2 rounded-[3px] bg-emerald-600 px-4 py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-emerald-700"
                >
                  <Headphones className="h-5 w-5" />
                  Run Echo Test
                </button>
              ) : (
                <button
                  onClick={softphone.stopEchoTest}
                  className="flex w-full items-center justify-center gap-2 rounded-[3px] bg-red-500 px-4 py-3 text-sm font-bold text-white shadow-sm transition-colors hover:bg-red-600"
                >
                  <VolumeX className="h-5 w-5" />
                  Stop Echo Test
                </button>
              )}
              <button
                onClick={() => { void logoutSession().then(() => navigate('/login')) }}
                className="flex w-full items-center justify-center gap-2 rounded-[3px] border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50"
              >
                <LogOut className="h-4 w-4" />
                Sign Out
              </button>
            </div>
            <p className="mt-4 text-center text-xs text-slate-400">
              Speak to hear your voice echoed back. This confirms your microphone and speakers are working.
            </p>
          </div>
        </div>
      )}
      {/* No global ScreenPop: an answered call must not cover the workspace.
          Contact/ticket creation is a manual action — the softphone panel
          exposes Create Contact / Create Ticket buttons that open drawers. */}
      <BrowserNotificationPrompt />
    </div>
  )
}
